// ─── Retreat availability and price options ──────────────────────────────────
// A retreat is either FIXED (start_date → end_date, one cohort) or FLEXIBLE
// (runs year-round or within a window; each guest picks a start day and stays
// duration_days). And it has one price, or several price options (dorm,
// private room, bungalow…) in the retreat's currency.
//
// Everything that needs to know "when does it run", "can a stay start on this
// day", "is it still listed" or "what does it cost from" asks this module, so
// the dashboard, browse, the retreat page and the enquiry agree.
// Dates are YYYY-MM-DD strings handled in LOCAL time throughout: toISOString()
// is UTC and shifts dates by a day east or west of Greenwich.

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const isFlexible = (r) => r?.availability_type === "flexible";

const pad = (n) => String(n).padStart(2, "0");
export const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};
const todayISO = () => isoLocal(new Date());

/** The day a stay of `days` days starting on `startISO` ends (inclusive): 3 Feb + 8 days → 10 Feb. */
export function stayEnd(startISO, days) {
  const s = parse(startISO);
  const n = Math.max(1, parseInt(days, 10) || 1);
  if (!s) return null;
  return isoLocal(new Date(s.getFullYear(), s.getMonth(), s.getDate() + n - 1));
}

/** Arrival weekdays as numbers 0–6; [] means any day. */
export const arrivalDays = (r) =>
  (Array.isArray(r?.arrival_days) ? r.arrival_days : []).map(Number).filter((n) => n >= 0 && n <= 6);

/** Is the flexible window closed for good (available_until in the past)? */
export function flexibleWindowOver(r, today = todayISO()) {
  return isFlexible(r) && !!r.available_until && r.available_until < today;
}

/** Can a flexible stay start on this date? Window and arrival days, not before today. */
export function canArriveOn(r, iso, today = todayISO()) {
  if (!isFlexible(r) || !iso) return false;
  if (iso < today) return false;
  if (r.available_from && iso < r.available_from) return false;
  if (r.available_until && iso > r.available_until) return false;
  const days = arrivalDays(r);
  if (days.length) { const d = parse(iso); if (!d || !days.includes(d.getDay())) return false; }
  return true;
}

/** First date a flexible stay can start, from today on (null if none in the next 2 years). */
export function nextArrival(r, from = todayISO()) {
  if (!isFlexible(r)) return null;
  let d = parse(r.available_from && r.available_from > from ? r.available_from : from);
  for (let i = 0; d && i < 731; i++) {
    const iso = isoLocal(d);
    if (r.available_until && iso > r.available_until) return null;
    if (canArriveOn(r, iso, from)) return iso;
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  }
  return null;
}

const monthName = (iso, withYear) => parse(iso)?.toLocaleDateString("en-GB", withYear ? { month: "short", year: "numeric" } : { month: "short" });

/** "Year-round", "Available Mar–Oct", "Available Mar 2027–Oct 2027", "Available until 31 Oct". */
export function flexibleWindowLabel(r) {
  const from = r?.available_from, until = r?.available_until;
  if (!from && !until) return "Year-round";
  const fmtDay = (iso) => parse(iso)?.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (!from) return `Available until ${fmtDay(until)}`;
  if (!until) return `Available from ${fmtDay(from)}`;
  const sameYear = from.slice(0, 4) === until.slice(0, 4);
  return `Available ${monthName(from, !sameYear)}–${monthName(until, !sameYear)}${sameYear ? ` ${from.slice(0, 4)}` : ""}`;
}

/** "Any arrival day" or "Arrivals Mon, Sat". */
export function arrivalLabel(r) {
  const days = arrivalDays(r);
  return days.length ? `Arrivals ${days.sort((a, b) => a - b).map((d) => WEEKDAYS[d]).join(", ")}` : "Any arrival day";
}

/** "8 days", or "1 day". */
export const daysLabel = (n) => { const d = parseInt(n, 10); return d > 0 ? `${d} day${d === 1 ? "" : "s"}` : null; };

/** The line cards show instead of "9 Nov – 16 Nov": "Flexible dates · Year-round · 8 days". */
export function flexibleSummary(r) {
  return ["Flexible dates", flexibleWindowLabel(r), daysLabel(r?.duration_days)].filter(Boolean).join(" · ");
}

/** Does a retreat run at all during a month ("YYYY-MM")? Fixed: its dates; flexible: its window. */
export function runsInMonth(r, ym) {
  if (!ym) return true;
  const first = `${ym}-01`;
  const last = isoLocal(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0));
  if (isFlexible(r)) {
    if (r.available_from && r.available_from > last) return false;
    if (r.available_until && r.available_until < first) return false;
    return true;
  }
  const s = r?.start_date, e = r?.end_date || r?.start_date;
  if (!s) return false;
  return s <= last && e >= first;
}

/** Days between a fixed retreat's start and end, inclusive (16 Nov – 9 Nov → 8). */
export function fixedSpanDays(start, end) {
  const s = parse(start), e = parse(end);
  if (!s || !e) return null;
  return Math.round((e - s) / 86400000) + 1;
}

// ── Price options ────────────────────────────────────────────────────────────

/** Options as stored, cleaned and ordered. [] when the retreat has a single price. */
export function priceOptions(r) {
  const list = Array.isArray(r?.price_options) ? r.price_options : [];
  return list
    .filter((o) => o && String(o.label || "").trim() && Number(o.price) > 0)
    .map((o, i) => ({ ...o, price: Number(o.price), sort_order: Number.isFinite(Number(o.sort_order)) ? Number(o.sort_order) : i }))
    .sort((a, b) => a.sort_order - b.sort_order);
}

/** The cheapest price: the lowest option when there are options, else `price`. */
export function fromPrice(r) {
  const opts = priceOptions(r);
  return opts.length ? Math.min(...opts.map((o) => o.price)) : Number(r?.price) || 0;
}

/** True when cards should say "from" because there is more than one price. */
export const hasSeveralPrices = (r) => priceOptions(r).length > 1;

/** The option a form should start on: the one marked default, else the first. */
export function defaultOption(r) {
  const opts = priceOptions(r);
  return opts.find((o) => o.is_default) || opts[0] || null;
}

// ── Queries ──────────────────────────────────────────────────────────────────
/**
 * PostgREST filter for "still listed": a fixed retreat that has not ended, or a
 * flexible one whose window has not closed. Replaces `end_date=gte.<today>`,
 * which a flexible retreat (end_date NULL) never satisfies.
 */
export const liveRetreatFilter = (today) =>
  `or=(end_date.gte.${today},and(availability_type.eq.flexible,or(available_until.is.null,available_until.gte.${today})))`;

/**
 * The month filter. Fixed retreats keep the rule they always had (start date
 * in that month); flexible ones match any month their window touches.
 */
export const matchesMonth = (r, ym) =>
  !ym ? true : isFlexible(r) ? runsInMonth(r, ym) : String(r?.start_date || "").slice(0, 7) === ym;

/** What to sort by "soonest": the start date, or a flexible retreat's next possible arrival. */
export const sortDate = (r) => (isFlexible(r) ? nextArrival(r) : r?.start_date) || "9999-12-31";
